import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ContentLayout from '../components/ContentLayout';

const WhatIsToon: React.FC = () => (
  <>
    <PageSEO
      path="/what-is-toon"
      title="What is TOON (Token-Oriented Object Notation)? A Plain-English Guide"
      description="TOON is a compact, LLM-friendly data format that cuts token usage 30-60% versus JSON. Learn what TOON is, how it works, and when to use it for GPT-4, Claude, and Gemini."
      keywords="what is TOON, token-oriented object notation, TOON format, TOON explained, LLM data format"
    />
    <ContentLayout
      related={[
        { to: '/json-vs-toon', label: 'JSON vs TOON: a side-by-side comparison' },
        { to: '/toon-format-spec', label: 'TOON format spec and syntax rules' },
        { to: '/reduce-llm-token-costs', label: 'How to reduce LLM token costs' },
      ]}
    >
      <h1>What is TOON (Token-Oriented Object Notation)?</h1>
      <p className="content-lead">
        TOON, short for Token-Oriented Object Notation, is a compact text format for structured
        data. It carries the same information as JSON but strips the redundant punctuation that
        large language models pay for by the token — typically reducing token usage by 30-60%.
      </p>

      <h2>The problem TOON solves</h2>
      <p>
        When you send structured data to an LLM like GPT-4, Claude, or Gemini, you pay per token.
        JSON spends a large share of those tokens on syntax that carries no information: opening and
        closing braces, square brackets, commas between every field, and quotation marks around
        every key and string. For an array of objects, JSON also repeats every field name on every
        single record.
      </p>
      <p>
        TOON keeps the data and throws away the ceremony. Structure is expressed through
        indentation (like YAML), quotes are dropped where they are unnecessary, and arrays of
        objects declare their field names once as a header instead of repeating them per row.
      </p>

      <h2>A quick example</h2>
      <p>The same data, in JSON and in TOON:</p>
      <pre><code>{`// JSON — 38 tokens (approx)
{
  "users": [
    { "id": 1, "name": "Ada",  "active": true },
    { "id": 2, "name": "Linus","active": false }
  ]
}

// TOON — ~22 tokens (approx)
users:
  - id: 1
    name: Ada
    active: true
  - id: 2
    name: Linus
    active: false`}</code></pre>
      <p>
        The data is identical and the conversion is lossless — you can turn TOON back into the exact
        same JSON. Only the format changed.
      </p>

      <h2>Why LLMs handle TOON well</h2>
      <p>
        TOON looks a lot like YAML and indented outlines that appear throughout model training data,
        so models parse it naturally. Independent benchmarks have shown comparable — sometimes
        slightly better — extraction accuracy with TOON than with JSON, while using far fewer
        tokens. Fewer tokens means lower cost and more room in the context window for actual content.
      </p>

      <div className="content-callout">
        <p>
          <strong>When TOON helps most:</strong> arrays of similar objects (tables, records, API
          responses), RAG context, chat history, and any prompt where you are pasting large
          structured payloads. For a single tiny object, the savings are small.
        </p>
      </div>

      <h2>When to use it (and when not to)</h2>
      <ul>
        <li><strong>Great fit:</strong> feeding tabular or repeated data into prompts, compressing RAG context, trimming long tool outputs before they re-enter the model.</li>
        <li><strong>Neutral:</strong> tiny payloads where token count barely matters.</li>
        <li><strong>Not a fit:</strong> machine-to-machine APIs between your own services — there, stick with JSON. TOON is for the LLM boundary.</li>
      </ul>

      <h2>Try it on your own data</h2>
      <p>
        The fastest way to understand TOON is to paste a real JSON payload into the{' '}
        <Link to="/">free converter</Link> and watch the live token count drop. Everything runs in
        your browser — your data never leaves your machine.
      </p>
    </ContentLayout>
  </>
);

export default WhatIsToon;
